import { ButtonGroup, IconButton, LuIcon, radiusCva, useProvider, toast } from "@overmap-ai/blocks"
import { NodeViewContent, type NodeViewProps, NodeViewWrapper } from "@tiptap/react"
import { cx } from "class-variance-authority"
import { memo, useCallback, useRef, useState } from "react"
import { twMerge } from "tailwind-merge"

import { modelIconMapping } from "../../../mappings/icons"
import { sdk } from "../../../sdk"

export const AgentResponseBubbleNodeComponent = memo((props: NodeViewProps) => {
	const { node } = props
	const { rating, response_id } = node.attrs as {
		rating?: 1 | 5 | undefined
		response_id: string
	}
	const { radius } = useProvider()

	const wrapperRef = useRef<HTMLDivElement | null>(null)

	// TODO: Rework this
	const handleCopy = useCallback(async () => {
		const wrapper = wrapperRef.current
		if (!wrapper) return
		let success = false
		try {
			// Prefer concatenating Markdown nodes when present (stable selector via node class)
			const mdNodeEls = wrapper.querySelectorAll<HTMLElement>(".node-MarkdownNode")
			let html = ""
			let plainText = ""
			if (mdNodeEls.length) {
				const parts = Array.from(mdNodeEls).map((nodeEl) => {
					const wrap = nodeEl.querySelector<HTMLElement>("[data-node-view-wrapper]")
					const contentEl = wrap?.querySelector<HTMLElement>("div")
					return {
						html: contentEl?.innerHTML ?? wrap?.innerHTML ?? nodeEl.innerHTML,
						text: (contentEl?.innerText ?? wrap?.innerText ?? nodeEl.innerText ?? "").trim(),
					}
				})
				html = parts.map((p) => p.html).join("\n\n")
				plainText = parts.map((p) => p.text).join("\n\n")
			} else {
				const root =
					wrapper.querySelector<HTMLElement>("[data-node-view-content]") ||
					wrapper.querySelector<HTMLElement>("[data-node-view-content-react]") ||
					wrapper
				if (root) {
					html = root.innerHTML.trim()
					plainText = root.innerText.trim()
				}
			}

			// Include raw markdown from children when available
			let markdown: string | undefined
			try {
				const children = node.content?.content ?? []
				const mdParts = children
					.filter((c) => c?.type?.name === "MarkdownNode" && typeof c?.attrs?.markdown === "string")
					.map((c) => String(c.attrs.markdown))
				if (mdParts.length) markdown = mdParts.join("\n\n")
			} catch {
				// ignore
			}

			if (!html && !plainText && !markdown) {
				// final fallback: text from ProseMirror node
				plainText = node.textContent
			}

			// Attempt modern API first (requires secure context)
			if (
				!success &&
				window.isSecureContext &&
				"clipboard" in navigator &&
				typeof window.ClipboardItem !== "undefined"
			) {
				try {
					const data: Record<string, Blob> = {}
					if (html) data["text/html"] = new Blob([html], { type: "text/html" })
					if (plainText) data["text/plain"] = new Blob([plainText], { type: "text/plain" })
					if (markdown) data["text/markdown"] = new Blob([markdown], { type: "text/markdown" })
					await navigator.clipboard.write([new window.ClipboardItem(data)])
					success = true
				} catch {
					// continue to fallbacks
				}
			}

			// Selection-based fallback for HTML copy (works in non-secure contexts)
			if (!success && html) {
				try {
					const temp = document.createElement("div")
					temp.style.position = "fixed"
					temp.style.left = "-99999px"
					temp.style.top = "0"
					temp.innerHTML = html
					document.body.appendChild(temp)
					const range = document.createRange()
					range.selectNodeContents(temp)
					const sel = window.getSelection()
					sel?.removeAllRanges()
					sel?.addRange(range)
					success = document.execCommand("copy")
					sel?.removeAllRanges()
					document.body.removeChild(temp)
				} catch {
					success = false
				}
			}

			// Fallback: writeText (prefer plain text to avoid raw tags in consumers)
			if (!success && "clipboard" in navigator && typeof navigator.clipboard.writeText === "function") {
				try {
					await navigator.clipboard.writeText(plainText || markdown || html || "")
					success = true
				} catch {
					// final failure
				}
			}

			if (success) {
				toast.success({ title: "Copied", description: "Response copied to clipboard." })
			} else {
				toast.error({ title: "Copy failed", description: "Couldn’t copy the response." })
			}
		} catch {
			toast.error({ title: "Copy failed", description: "Couldn’t copy the response." })
		}
	}, [node])

	return (
		<NodeViewWrapper>
			<div
				className={twMerge(
					cx("bg-(--base-2) mb-2 flex flex-col font-normal", radiusCva({ radius, maxLarge: true })),
				)}
				ref={wrapperRef}
			>
				<NodeViewContent />
				<ButtonGroup className="flex items-center" size="sm" variant="ghost" accentColor="base">
					<AgentResponseRating responseId={response_id} key={rating} rating={rating} />
					<IconButton onClick={handleCopy} aria-label="Copy response for documentation.">
						<LuIcon icon={modelIconMapping.copy} />
					</IconButton>
				</ButtonGroup>
			</div>
		</NodeViewWrapper>
	)
})

AgentResponseBubbleNodeComponent.displayName = "AgentResponseBubbleNodeComponent"

interface AgentResponseRatingNodeComponentProps {
	rating?: 1 | 5 | undefined
	responseId: string
}

const AgentResponseRating = memo((props: AgentResponseRatingNodeComponentProps) => {
	const { rating: ratingFromServer, responseId } = props

	const [rating, setRating] = useState<1 | 5 | undefined>(ratingFromServer)

	const handleUpVote = useCallback(() => {
		if (rating !== 5) {
			// TODO: Implement deletion of ratings?
			const oldRating = rating
			setRating(5)
			sdk.agents.rate(responseId, 5).catch(() => {
				setRating(oldRating)
			})
		}
	}, [rating, responseId])

	const handleDownVote = useCallback(() => {
		if (rating !== 1) {
			// TODO: Implement deletion of ratings?
			const oldRating = rating
			setRating(1)
			sdk.agents.rate(responseId, 1).catch(() => {
				setRating(oldRating)
			})
		}
	}, [rating, responseId])

	return (
		<ButtonGroup className="flex items-center" size="sm" variant="ghost" accentColor="base">
			<IconButton onClick={handleUpVote} aria-label="This response was helpful.">
				<LuIcon icon={modelIconMapping.thumbsUp} fill={rating === 5 ? "inherit" : undefined} />
			</IconButton>
			<IconButton onClick={handleDownVote} aria-label="This response was not helpful.">
				<LuIcon icon={modelIconMapping.thumbsDown} fill={rating === 1 ? "inherit" : undefined} />
			</IconButton>
		</ButtonGroup>
	)
})

AgentResponseRating.displayName = "AgentResponseRating"
